/**
 * /admin/users — B2C user management.
 *
 * Search → list (paginated) → click row → side drawer with full P&L,
 * subscription, wallet balance, recent ledger, and admin actions
 * (suspend / reactivate / reset password).
 *
 * Backend: GET /api/admin/v2/finances/users/ for the enriched list
 * (one query returns plan + wallet + storage + AWS proxy cost) and
 * GET /api/admin/v2/finances/users/<id>/ for the drawer payload.
 * Mutation endpoints (set-active / reset-password) live under the
 * older /api/admin/users/ namespace.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X, Shield, ShieldOff, KeyRound, RefreshCw, ExternalLink, UserCog, Download, FileSpreadsheet, Trophy, Trash2, CreditCard, Receipt, Repeat, Package, Undo2, FileText, ArrowUp, ArrowDown } from "lucide-react";
import useDialogDismiss from "../../hooks/useDialogDismiss";
import {
  listUserFinancials,
  getUserFinancials,
  setUserActive,
  resetUserPassword,
  deleteUser,
  revokeUserDelete,
  hardDeleteUser,
  getDashboardSummary,
  listAdminUserMatches,
  downloadAdminMatchXlsx,
  setUserTabOverride,
} from "../../api/owner.api";

function fmtInr(amount) {
  if (amount == null) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency: "INR", maximumFractionDigits: 0,
  }).format(amount);
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

// Compact trial-end formatter — "in 2d" / "in 5h" / "expired 1d ago" /
// "DD MMM" for far-future. Used inline next to the plan code so the
// operator can see at a glance when each trial converts.
function fmtTrialEnd(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffMs = d - Date.now();
  const diffH = Math.round(diffMs / 3.6e6);
  const diffD = Math.round(diffMs / 8.64e7);
  if (diffMs < 0) {
    const abs = Math.abs(diffH);
    if (abs < 24) return `expired ${abs}h ago`;
    return `expired ${Math.abs(diffD)}d ago`;
  }
  if (diffH < 24) return `in ${diffH}h`;
  if (diffD < 7) return `in ${diffD}d`;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

// Format a number of ZenCredits — thousands separator + trailing "ZC".
function fmtZC(n) {
  if (n == null) return "—";
  return `${Number(n).toLocaleString("en-IN")} ZC`;
}

// Format storage as GB with two decimal precision; show "—" when zero
// so the row doesn't visually noise with rows of "0.00 GB".
function fmtGB(gb) {
  if (gb == null || gb === 0) return "—";
  return `${gb.toFixed(2)} GB`;
}

function fmtUsdMo(usd) {
  if (usd == null || usd === 0) return "—";
  return `$${usd.toFixed(3)}/mo`;
}

const SEGMENTS = [
  { key: "b2c",    label: "B2C",     hint: "Solo subscribers" },
  { key: "b2b",    label: "B2B",     hint: "Organization users" },
  { key: "player", label: "Players", hint: "Self-signed-up athletes" },
  { key: "staff",  label: "Staff",   hint: "Manivannan + sub-admins" },
  { key: "all",    label: "All",     hint: "Every user" },
];

function planStatusLabel(v) {
  return ({
    paid:      "Paid sub",
    trial:     "On trial",
    team_code: "Team-code grant",
    none:      "No plan",
  })[v] || v;
}

function joinedWithinLabel(v) {
  return ({
    "24h":  "last 24h",
    "7d":   "last 7 days",
    "30d":  "last 30 days",
    "90d":  "last 90 days",
    "365d": "last year",
  })[v] || v;
}

function FilterChip({ label, onClear }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-border-default bg-bg-secondary text-text-secondary">
      {label}
      <button
        type="button"
        onClick={onClear}
        className="hover:text-text-primary"
        aria-label={`Clear filter: ${label}`}
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

export default function AdminUsers() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Deep-link from the Dashboard segment cards via ?segment=b2c|b2b|staff|all.
  // Falls back to "b2c" to preserve the page's previous default.
  const segmentParam = searchParams.get("segment");
  const initialSegment = ["b2c", "b2b", "player", "staff", "all"].includes(segmentParam)
    ? segmentParam : "b2c";
  const [search, setSearch] = useState("");
  const [active, setActive] = useState("");           // "", true, false
  const [planStatus, setPlanStatus] = useState("");   // "", paid, trial, team_code, none
  const [joinedWithin, setJoinedWithin] = useState(""); // "", 24h, 7d, 30d, 90d, 365d
  const [walletFilter, setWalletFilter] = useState(""); // "", has_balance, empty
  const [segment, setSegment] = useState(initialSegment);
  // "-joined" = newest first (default, matches prior behaviour);
  // "joined" = oldest first. Other sortable columns ("-balance" /
  // "balance") cycle desc → asc → desc via toggleSort below.
  const [order, setOrder] = useState("-joined");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [segCounts, setSegCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);  // user row from list

  // Pull segment counts once on mount (and on Refresh) so the tab
  // labels carry live numbers. Cheap — single DashboardSummary call.
  useEffect(() => {
    getDashboardSummary()
      .then(({ data }) => setSegCounts(data?.users || null))
      .catch(() => setSegCounts(null));
  }, []);

  // Debounced search + segment/active/plan/joined/wallet/order filter
  // — re-query 250ms after the operator stops typing or flips a filter.
  // Order toggles are debounced too so a rapid double-click doesn't
  // double-fetch.
  useEffect(() => {
    const t = setTimeout(() => fetchPage(1), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, active, segment, order, planStatus, joinedWithin, walletFilter]);

  // Reflect segment in URL so refresh + back-button keep the tab state,
  // and the dashboard cards' deep-links round-trip correctly.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (segment === "b2c") next.delete("segment");
    else next.set("segment", segment);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segment]);

  useEffect(() => { fetchPage(page); /* eslint-disable-next-line */ }, [page]);

  async function fetchPage(p) {
    setLoading(true);
    try {
      const { data } = await listUserFinancials({
        search: search || undefined,
        is_active: active || undefined,
        plan_status: planStatus || undefined,
        joined_within: joinedWithin || undefined,
        wallet: walletFilter || undefined,
        segment,
        order,
        page: p,
      });
      setRows(data.results || []);
      setCount(data.count || 0);
      if (p === 1 && page !== 1) setPage(1);
    } finally {
      setLoading(false);
    }
  }

  // True when ANY non-default filter is set — drives the "Clear filters"
  // button visibility + the active-filter pill row.
  const filtersActive = !!(
    search || active || planStatus || joinedWithin || walletFilter
  );

  function clearAllFilters() {
    setSearch("");
    setActive("");
    setPlanStatus("");
    setJoinedWithin("");
    setWalletFilter("");
  }

  // Generic sort toggler — cycles desc → asc → desc on the same column
  // and resets to "-<col>" when switching columns.
  function toggleSort(col) {
    setOrder((prev) => {
      if (prev === `-${col}`) return col;
      if (prev === col) return `-${col}`;
      return `-${col}`;
    });
  }

  function refreshAll() {
    getDashboardSummary()
      .then(({ data }) => setSegCounts(data?.users || null))
      .catch(() => {});
    fetchPage(page);
  }

  const totalPages = Math.max(1, Math.ceil(count / 25));
  const segmentLabel = SEGMENTS.find((s) => s.key === segment)?.label || segment;

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Users — {segmentLabel}</h1>
          <p className="text-sm text-text-secondary mt-1">
            {count.toLocaleString("en-IN")} in this segment. Plan + wallet +
            storage at a glance; click a row for the full P&amp;L drawer.
          </p>
        </div>
        <button
          onClick={refreshAll}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border-default text-sm hover:bg-bg-secondary"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </header>

      {/* Segment tabs — pills with live counts from DashboardSummary. */}
      <div className="flex flex-wrap gap-2">
        {SEGMENTS.map((s) => {
          const isActive = s.key === segment;
          const segCount =
            s.key === "all"
              ? segCounts?.total
              : segCounts?.[s.key];
          return (
            <button
              key={s.key}
              onClick={() => setSegment(s.key)}
              title={s.hint}
              aria-label={`Filter to ${s.label}${segCount != null ? ` — ${segCount} user(s)` : ""}`}
              aria-pressed={isActive}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                isActive
                  ? "bg-button-primary text-white border-button-primary"
                  : "bg-bg-primary border-border-default text-text-secondary hover:text-text-primary hover:border-text-secondary"
              }`}
            >
              {s.label}
              {segCount != null && (
                <span className={`ml-2 text-xs font-mono ${isActive ? "opacity-80" : "opacity-60"}`}>
                  {segCount.toLocaleString("en-IN")}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Email or name…"
            className="w-full pl-9 pr-3 py-2 rounded-md bg-input border border-border-default text-sm focus:outline-none focus:border-button-primary"
          />
        </div>
        <select
          value={active}
          onChange={(e) => setActive(e.target.value)}
          className="text-sm"
          title="Account status"
        >
          <option value="">Any status</option>
          <option value="true">Active</option>
          <option value="false">Suspended</option>
        </select>
        <select
          value={planStatus}
          onChange={(e) => setPlanStatus(e.target.value)}
          className="text-sm"
          title="Current subscription slice"
        >
          <option value="">Any plan</option>
          <option value="paid">Paid sub</option>
          <option value="trial">On trial</option>
          <option value="team_code">Team-code grant</option>
          <option value="none">No plan</option>
        </select>
        <select
          value={joinedWithin}
          onChange={(e) => setJoinedWithin(e.target.value)}
          className="text-sm"
          title="Filter by signup date"
        >
          <option value="">Any signup date</option>
          <option value="24h">Joined last 24h</option>
          <option value="7d">Joined last 7 days</option>
          <option value="30d">Joined last 30 days</option>
          <option value="90d">Joined last 90 days</option>
          <option value="365d">Joined last year</option>
        </select>
        <select
          value={walletFilter}
          onChange={(e) => setWalletFilter(e.target.value)}
          className="text-sm"
          title="Wallet balance slice"
        >
          <option value="">Any wallet</option>
          <option value="has_balance">Has ZC balance</option>
          <option value="empty">Empty wallet</option>
        </select>
        {filtersActive && (
          <button
            type="button"
            onClick={clearAllFilters}
            className="text-xs text-text-secondary hover:text-text-primary px-2 py-1 inline-flex items-center gap-1"
            title="Reset every filter (segment + sort untouched)"
          >
            <X className="w-3 h-3" />
            Clear filters
          </button>
        )}
      </div>

      {/* Active-filter pill row — one chip per active filter, click to
          clear that one. Keeps it obvious WHY the table is narrowed
          when the operator returns to the tab. */}
      {filtersActive && (
        <div className="flex flex-wrap gap-1.5 items-center text-[11px]">
          {search && (
            <FilterChip
              label={`Search: "${search}"`}
              onClear={() => setSearch("")}
            />
          )}
          {active && (
            <FilterChip
              label={active === "true" ? "Active only" : "Suspended only"}
              onClear={() => setActive("")}
            />
          )}
          {planStatus && (
            <FilterChip
              label={`Plan: ${planStatusLabel(planStatus)}`}
              onClear={() => setPlanStatus("")}
            />
          )}
          {joinedWithin && (
            <FilterChip
              label={`Joined ${joinedWithinLabel(joinedWithin)}`}
              onClear={() => setJoinedWithin("")}
            />
          )}
          {walletFilter && (
            <FilterChip
              label={walletFilter === "has_balance" ? "Has ZC balance" : "Empty wallet"}
              onClear={() => setWalletFilter("")}
            />
          )}
        </div>
      )}

      {/* Table — every row is one B2C user with their plan, wallet,
          storage and proxy AWS cost. Click → drawer with full P&L.
          AWS / Joined / Status hidden under md so iPad portrait + phones
          aren't forced into horizontal scroll (2026-05-27 audit). */}
      <div className="rounded-lg border border-border-default bg-bg-primary overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-table-header">
            <tr className="text-left text-[11px] uppercase tracking-widest text-text-secondary">
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Plan</th>
              <th className="px-3 py-2 text-right">
                <button
                  type="button"
                  onClick={() => toggleSort("balance")}
                  title={
                    order === "-balance"
                      ? "Sorted highest balance first — click for lowest first"
                      : order === "balance"
                        ? "Sorted lowest balance first — click for highest first"
                        : "Sort by ZC balance"
                  }
                  className="inline-flex items-center gap-1 uppercase tracking-widest text-[11px] text-text-secondary hover:text-text-primary"
                >
                  ZC balance
                  {order === "-balance" ? (
                    <ArrowDown className="w-3 h-3" />
                  ) : order === "balance" ? (
                    <ArrowUp className="w-3 h-3" />
                  ) : null}
                </button>
              </th>
              <th className="px-3 py-2 text-right hidden lg:table-cell">ZC spent (life)</th>
              <th className="px-3 py-2 text-right">Storage</th>
              <th className="px-3 py-2 text-right hidden md:table-cell">AWS (proxy)</th>
              <th className="px-3 py-2 hidden md:table-cell">
                <button
                  type="button"
                  onClick={() => setOrder((o) => (o === "-joined" ? "joined" : "-joined"))}
                  title={
                    order === "-joined"
                      ? "Sorted newest first — click for oldest first"
                      : "Sorted oldest first — click for newest first"
                  }
                  className="inline-flex items-center gap-1 uppercase tracking-widest text-[11px] text-text-secondary hover:text-text-primary"
                >
                  Joined
                  {order === "-joined" ? (
                    <ArrowDown className="w-3 h-3" />
                  ) : order === "joined" ? (
                    <ArrowUp className="w-3 h-3" />
                  ) : null}
                </button>
              </th>
              <th className="px-3 py-2 hidden md:table-cell">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-text-secondary">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-text-secondary">No users match.</td></tr>
            )}
            {rows.map((u) => {
              const plan = u.plan || {};
              const wallet = u.wallet || {};
              const storage = u.storage || {};
              // payment_provider === null on a paid-tier plan means
              // the sub came from a team-code redemption — flag visually
              // so the operator knows it isn't real revenue.
              const isTeamCodeSub = plan.plan_code && !plan.payment_provider;
              return (
                <tr
                  key={u.user_id}
                  // Drawer reads `id` (legacy /api/admin/users/ shape) so
                  // map the v2 endpoint's `user_id` over without
                  // touching the drawer code.
                  onClick={() => setSelected({ ...u, id: u.user_id, date_joined: u.joined })}
                  className="border-t border-border-default hover:bg-bg-secondary cursor-pointer"
                >
                  <td className="px-3 py-2 font-mono text-xs">
                    <div>{u.email}</div>
                    <div className="text-text-secondary">{u.full_name}</div>
                  </td>
                  <td className="px-3 py-2">
                    {plan.plan_code ? (
                      <div>
                        <div className="text-xs">{plan.plan_code}</div>
                        {isTeamCodeSub && (
                          <div className="text-[10px] uppercase tracking-wider text-amber-300">
                            team code
                          </div>
                        )}
                        {plan.is_trial && plan.trial_ends_at && (
                          <div
                            className="text-[10px] mt-0.5 text-emerald-300"
                            title={`Trial ends ${new Date(plan.trial_ends_at).toLocaleString("en-IN")}`}
                          >
                            trial ends {fmtTrialEnd(plan.trial_ends_at)}
                            {plan.target_plan_code && (
                              <span className="text-text-secondary"> → {plan.target_plan_code}</span>
                            )}
                          </div>
                        )}
                        {plan.status === "PENDING" && (
                          <div className="text-[10px] uppercase tracking-wider text-amber-300">
                            mandate pending
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-text-secondary text-xs">No plan</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtZC(wallet.balance)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-text-secondary hidden lg:table-cell">
                    {fmtZC(wallet.lifetime_spent)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtGB(storage.gb)}</td>
                  <td
                    className="px-3 py-2 text-right tabular-nums text-text-secondary hidden md:table-cell"
                    title="Proxy: storage GB × $0.023/GB-mo (S3 Standard list rate). Not exact AWS bill split."
                  >
                    {fmtUsdMo(storage.aws_storage_usd_mo)}
                  </td>
                  <td className="px-3 py-2 text-text-secondary text-xs hidden md:table-cell">{fmtDate(u.joined)}</td>
                  <td className="px-3 py-2 hidden md:table-cell">
                    {u.is_active
                      ? <span className="text-emerald-400 text-xs">● Active</span>
                      : <span className="text-red-400 text-xs">● Suspended</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-xs text-text-secondary">
        <div>Page {page} of {totalPages}</div>
        <div className="flex gap-1">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1 rounded border border-border-default disabled:opacity-40 hover:bg-bg-secondary"
          >Prev</button>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="px-3 py-1 rounded border border-border-default disabled:opacity-40 hover:bg-bg-secondary"
          >Next</button>
        </div>
      </div>

      {selected && (
        <UserDrawer
          user={selected}
          onClose={() => setSelected(null)}
          onChanged={() => fetchPage(page)}
        />
      )}
    </div>
  );
}

// ─── Per-user drawer with P&L + admin actions ────────────────────────────

function UserDrawer({ user, onClose, onChanged }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tempPwd, setTempPwd] = useState(null);
  // 2026-06-11 — local draft of the segment override. Initialised from
  // the detail payload once it loads, then edited locally until Save.
  const [overrideDraft, setOverrideDraft] = useState("");
  const [overrideBusy, setOverrideBusy] = useState(false);
  const panelRef = useRef(null);
  // 2026-06-08 — drawer was unreachable by keyboard / Android-back;
  // hook adds Esc / hardware-back / focus-trap.
  useDialogDismiss({ open: true, onClose, ref: panelRef });

  // 2026-06-08 — Matches section. The matches list loads in parallel
  // with financials so the drawer paints without one blocking the other.
  // matchesUI status:
  //   null      — still loading
  //   []        — loaded, empty
  //   [rows…]   — loaded
  //   "error"   — fetch failed (rare; render a one-line error inline)
  const [matchesUI, setMatchesUI] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getUserFinancials(user.id)
      .then((r) => {
        if (!alive) return;
        setDetails(r.data);
        setOverrideDraft((r.data?.user?.tab_override || "").toUpperCase());
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [user.id]);

  useEffect(() => {
    let alive = true;
    setMatchesUI(null);
    listAdminUserMatches(user.id)
      .then((r) => { if (alive) setMatchesUI(r.data?.matches || []); })
      .catch(() => { if (alive) setMatchesUI("error"); });
    return () => { alive = false; };
  }, [user.id]);

  const handleDownloadMatch = async (matchId) => {
    if (downloadingId) return;
    setDownloadingId(matchId);
    try {
      await downloadAdminMatchXlsx(matchId);
    } catch (e) {
      alert(`Download failed: ${e?.response?.data?.detail || e?.message || e}`);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleToggleActive = async () => {
    setBusy(true);
    try {
      await setUserActive(user.id, !user.is_active);
      onChanged();
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const handleResetPassword = async () => {
    if (!confirm(`Generate a new password for ${user.email}? They'll need it to log in next.`)) return;
    setBusy(true);
    try {
      const { data } = await resetUserPassword(user.id);
      setTempPwd(data.temp_password);
    } finally {
      setBusy(false);
    }
  };

  const handleSaveTabOverride = async () => {
    if (overrideBusy) return;
    const current = (details?.user?.tab_override || "").toUpperCase();
    const next = (overrideDraft || "").toUpperCase();
    if (current === next) return;
    setOverrideBusy(true);
    try {
      const { data } = await setUserTabOverride(user.id, next);
      // Reflect the new value in the drawer + refresh the list so the
      // user moves tab immediately if the operator is on the segment
      // they were just classified out of.
      setDetails((prev) => prev ? ({
        ...prev,
        user: { ...prev.user, tab_override: data?.tab_override || "" },
      }) : prev);
      onChanged();
    } catch (e) {
      alert(`Couldn't update tab override: ${e?.response?.data?.error || e?.message || e}`);
    } finally {
      setOverrideBusy(false);
    }
  };

  // Soft delete — reversible tombstone. Everything preserved; login
  // disabled; Cashfree mandate cancelled so no rogue debits fire.
  // The revoke button (below, when the user is soft-deleted) undoes it.
  const handleSoftDelete = async () => {
    if (!confirm(
      `Soft-delete ${user.email}?\n\n` +
      `Login disabled and the account is tombstoned, but the row + all ` +
      `PII (email, phone, password) stays in the DB. You can revoke ` +
      `this from the drawer anytime.\n\n` +
      `Any live Cashfree mandate is cancelled so the user isn't billed ` +
      `while suspended.\n\n` +
      `Click OK to proceed.`,
    )) return;
    const typed = prompt(
      `Type DELETE to confirm soft-delete of ${user.email}.`,
    );
    if ((typed || "").trim() !== "DELETE") return;
    setBusy(true);
    try {
      await deleteUser(user.id);
      onChanged();
      onClose();
    } catch (e) {
      alert(`Soft-delete failed: ${e?.response?.data?.error || e?.response?.data?.detail || e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  // Revoke a prior soft-delete. Only visible when the user is currently
  // tombstoned (deletion_kind === "ADMIN_SOFT" on the loaded user row).
  const handleRevokeDelete = async () => {
    if (!confirm(`Revoke the soft-delete on ${user.email}?\n\nLogin will be re-enabled.`)) return;
    setBusy(true);
    try {
      await revokeUserDelete(user.id);
      onChanged();
      onClose();
    } catch (e) {
      alert(`Revoke failed: ${e?.response?.data?.error || e?.response?.data?.detail || e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  // Hard delete — permanent. Backend refuses if the user has wallet
  // transactions, invoices, or coded matches; those blockers are
  // surfaced in the alert so the operator knows to soft-delete instead.
  const handleHardDelete = async () => {
    if (!confirm(
      `HARD DELETE the account for ${user.email}?\n\n` +
      `IRREVERSIBLE. The User row plus any cascading data (wallet, ` +
      `subscription rows without financial history, etc.) is removed ` +
      `from the DB. The email is fully released for re-signup.\n\n` +
      `Backend refuses this if the user has wallet transactions, ` +
      `invoices, or coded matches — those users can only be soft-deleted.\n\n` +
      `Click OK to proceed.`,
    )) return;
    const typed = prompt(
      `Type HARD DELETE in capitals to confirm permanent removal of ${user.email}.`,
    );
    if ((typed || "").trim() !== "HARD DELETE") return;
    setBusy(true);
    try {
      await hardDeleteUser(user.id);
      onChanged();
      onClose();
    } catch (e) {
      const body = e?.response?.data || {};
      if (body.error === "has_data" && Array.isArray(body.blockers)) {
        alert(
          `Hard delete refused.\n\nThis user has:\n  - ${body.blockers.join("\n  - ")}\n\n` +
          `Use soft-delete instead — data stays intact and the account is revocable.`,
        );
      } else {
        alert(`Hard delete failed: ${body.error || body.detail || e?.message || e}`);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="presentation">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={user.email}
        className="relative w-full max-w-md bg-bg-primary border-l border-border-default h-full overflow-y-auto"
      >
        <header className="flex items-start justify-between p-4 border-b border-border-default sticky top-0 bg-bg-primary">
          <div>
            <div className="font-mono text-sm">{user.email}</div>
            <div className="text-xs text-text-secondary">{user.full_name}</div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-bg-secondary">
            <X className="w-4 h-4" />
          </button>
        </header>

        {tempPwd && (
          <div className="m-4 p-3 rounded border border-amber-500/40 bg-amber-500/10 text-sm">
            <div className="text-amber-300 font-semibold mb-1">Temp password — save now</div>
            <div className="font-mono text-xs select-all break-all">{tempPwd}</div>
            <div className="text-[11px] text-text-secondary mt-1">
              This will not be shown again. Send it to the user via a secure channel.
            </div>
          </div>
        )}

        <div className="p-4 space-y-4">
          {loading && <div className="text-sm text-text-secondary">Loading P&amp;L…</div>}
          {details && (
            <>
              {/* Lifetime band — LTV + lifetime ZC topped up / spent.
                  Sized to fit above the MTD P&L without scrolling so
                  the operator clocks account size at a glance. Data
                  comes from `details.lifetime` (added 2026-06-22). */}
              {details.lifetime && (
                <section className="rounded-md border border-indigo-500/40 bg-indigo-500/5 p-2.5">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-[10px] uppercase tracking-widest text-indigo-300/80">
                      Lifetime (LTV)
                    </div>
                    <div className="font-mono text-base text-indigo-200">
                      {fmtInr(details.lifetime.ltv_inr)}
                    </div>
                  </div>
                  <div className="text-[11px] text-text-secondary mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                    <span>top-ups {fmtInr(details.lifetime.topups_inr)}</span>
                    <span>subs {fmtInr(details.lifetime.subscriptions_inr || 0)}</span>
                    <span>pack spend {fmtInr(details.lifetime.pack_sales_inr)}</span>
                    <span>topped up {details.lifetime.topped_up_zc} ZC</span>
                    <span>spent {details.lifetime.spent_zc} ZC</span>
                  </div>
                </section>
              )}

              {/* P&L */}
              <section>
                <h3 className="text-xs uppercase tracking-widest text-text-secondary mb-2">
                  Month-to-date P&amp;L
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="rounded border border-border-default p-2">
                    <div className="text-[10px] uppercase text-text-secondary">Revenue</div>
                    <div className="font-mono text-base">{fmtInr(details.revenue?.total_inr)}</div>
                    <div className="text-[10px] text-text-secondary mt-0.5">
                      top-ups {fmtInr(details.revenue?.topups_inr)} · subs {fmtInr(details.revenue?.subscriptions_inr || 0)}
                    </div>
                  </div>
                  <div className="rounded border border-border-default p-2">
                    <div className="text-[10px] uppercase text-text-secondary">Variable cost</div>
                    <div className="font-mono text-base">${details.cost?.total_usd?.toFixed(2) ?? "0.00"}</div>
                    <div className="text-[10px] text-text-secondary mt-0.5">
                      AI + storage + infra (phase 4-5)
                    </div>
                  </div>
                </div>
              </section>

              {/* Wallet + subscription */}
              <section className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded border border-border-default p-2">
                  <div className="text-[10px] uppercase text-text-secondary">Wallet</div>
                  <div className="font-mono text-base">{details.wallet_balance_zc} ZC</div>
                </div>
                <div className="rounded border border-border-default p-2">
                  <div className="text-[10px] uppercase text-text-secondary">Subscription</div>
                  <div className="text-sm">
                    {details.subscription?.plan_code || <span className="text-text-secondary">none</span>}
                  </div>
                  {details.subscription?.current_period_end && (
                    <div className="text-[10px] text-text-secondary">
                      until {fmtDate(details.subscription.current_period_end)}
                    </div>
                  )}
                </div>
              </section>

              {/* Tab override — manual classification (2026-06-11).
                  Empty = derived from rules. Set to force the user into
                  a specific Users-tab segment when the derived classifier
                  is wrong (e.g. a Player who got auto-trial-classified
                  into B2C, or a paying B2C customer who also claimed a
                  Player profile). */}
              <section className="rounded border border-border-default p-2">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[10px] uppercase tracking-widest text-text-secondary">
                    Tab override
                  </div>
                  {details.user?.tab_override ? (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-400/40">
                      forced: {details.user.tab_override}
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider text-text-secondary">
                      auto
                    </span>
                  )}
                </div>
                <div className="flex gap-2 items-center">
                  <select
                    value={overrideDraft}
                    onChange={(e) => setOverrideDraft(e.target.value)}
                    disabled={overrideBusy}
                    className="flex-1 text-sm bg-bg-secondary border border-border-default rounded px-2 py-1"
                  >
                    <option value="">Auto (derived from rules)</option>
                    <option value="B2C">Force B2C</option>
                    <option value="B2B">Force B2B</option>
                    <option value="PLAYER">Force Player</option>
                  </select>
                  <button
                    onClick={handleSaveTabOverride}
                    disabled={
                      overrideBusy
                      || (overrideDraft || "").toUpperCase()
                         === (details.user?.tab_override || "").toUpperCase()
                    }
                    className="px-3 py-1 rounded bg-button-primary text-white text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {overrideBusy ? "Saving…" : "Save"}
                  </button>
                </div>
                <div className="text-[10px] text-text-secondary mt-1">
                  Empty = derive from claim / org / paid-sub rules. Set
                  this only when the derived tab is wrong.
                </div>
              </section>

              {/* Recent ledger */}
              <section>
                <h3 className="text-xs uppercase tracking-widest text-text-secondary mb-2">
                  Recent wallet activity
                </h3>
                <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
                  {details.recent_ledger?.length === 0 && (
                    <div className="text-xs text-text-secondary">No activity.</div>
                  )}
                  {(details.recent_ledger || []).map((tx) => (
                    <LedgerRow key={tx.id} tx={tx} />
                  ))}
                </div>
              </section>

              {/* Full payment surface — subscriptions / top-ups /
                  pack purchases / refunds / invoices / payment events.
                  Backend serves this in details.payments. */}
              <PaymentSection payments={details.payments} />
            </>
          )}

          {/* 2026-06-08 — Matches section. Lists every match owned by
              this user and gives each row a Download Excel button that
              streams the .xlsx via downloadAdminMatchXlsx. */}
          <section className="mt-4">
            <h3 className="text-xs uppercase tracking-wider text-text-secondary mb-2 flex items-center gap-2">
              <Trophy className="w-3.5 h-3.5" />
              Matches
              {Array.isArray(matchesUI) && matchesUI.length > 0 && (
                <span className="text-text-primary font-mono">({matchesUI.length})</span>
              )}
            </h3>
            {matchesUI === null && (
              <div className="text-xs text-text-secondary">Loading matches…</div>
            )}
            {matchesUI === "error" && (
              <div className="text-xs text-rose-300">Failed to load matches.</div>
            )}
            {Array.isArray(matchesUI) && matchesUI.length === 0 && (
              <div className="text-xs text-text-secondary">No matches owned by this user.</div>
            )}
            {Array.isArray(matchesUI) && matchesUI.length > 0 && (
              <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                {matchesUI.map((m) => (
                  <div
                    key={m.id}
                    className="flex items-center gap-2 rounded border border-border-default bg-bg-secondary/40 px-2.5 py-1.5"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">
                        {(m.team1 && m.team2) ? `${m.team1} vs ${m.team2}` : "—"}
                      </div>
                      <div className="text-[10px] text-text-secondary flex flex-wrap gap-x-2 gap-y-0.5">
                        <span>{m.match_date || "no date"}</span>
                        {m.format && <span>· {m.format}</span>}
                        {m.state && <span>· {m.state}</span>}
                        {m.ball_count > 0 && <span>· {m.ball_count.toLocaleString("en-IN")} balls</span>}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDownloadMatch(m.id)}
                      disabled={downloadingId === m.id}
                      title="Download ball-by-ball as Excel"
                      className="shrink-0 text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-button-primary/15 border border-button-primary/40 text-button-primary hover:bg-button-primary/25 disabled:opacity-50 inline-flex items-center gap-1"
                    >
                      {downloadingId === m.id
                        ? <><RefreshCw className="w-3 h-3 animate-spin" /> …</>
                        : <><FileSpreadsheet className="w-3 h-3" /> Excel</>}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Actions */}
        <footer className="p-4 border-t border-border-default space-y-2 sticky bottom-0 bg-bg-primary">
          <button
            disabled={busy}
            onClick={handleToggleActive}
            className="w-full px-3 py-2 rounded border border-border-default text-sm hover:bg-bg-secondary disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {user.is_active
              ? <><ShieldOff className="w-4 h-4" /> Suspend account</>
              : <><Shield className="w-4 h-4" /> Reactivate account</>}
          </button>
          <button
            disabled={busy}
            onClick={handleResetPassword}
            className="w-full px-3 py-2 rounded border border-border-default text-sm hover:bg-bg-secondary disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <KeyRound className="w-4 h-4" /> Reset password
          </button>
          {/* Impersonation entry. Django-side because the middleware
              keys off the Django session — SPA uses JWT, so a true
              SPA-side impersonation needs a JWT-swap follow-up.
              Today this opens the Django admin user-change page in a
              new tab; the Impersonate form sits on that page. */}
          <a
            href={`https://api.zencricketdata.com/admin/accounts/user/${user.id}/change/`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full px-3 py-2 rounded border border-blue-500/40 text-blue-300 text-sm hover:bg-blue-500/10 flex items-center justify-center gap-2"
            title="Opens the Django admin user-change page where the Impersonate form lives. Session impersonation only — SPA continues as your own user."
          >
            <UserCog className="w-4 h-4" /> Impersonate (Django admin)
            <ExternalLink className="w-3 h-3 opacity-60" />
          </a>
          {/* Delete actions — three states, ordered by escalation:
              1. Soft delete (amber): reversible tombstone. Default.
              2. Revoke soft-delete (emerald): only shown when the user
                 is currently soft-deleted (deletion_kind === "ADMIN_SOFT").
              3. Hard delete (rose): permanent. Backend refuses if the
                 user has wallet transactions, invoices, or matches — so
                 this is really only for empty test accounts.
              Superusers can't be touched by any of them. */}
          {!user.is_superuser && (
            details?.user?.deletion_kind === "ADMIN_SOFT" ? (
              <button
                disabled={busy}
                onClick={handleRevokeDelete}
                className="w-full px-3 py-2 rounded border border-emerald-500/40 text-emerald-300 text-sm hover:bg-emerald-500/15 disabled:opacity-50 flex items-center justify-center gap-2"
                title="Restore login. is_active flips back on; canonical_email + phone were preserved so nothing else needs to change."
              >
                <Shield className="w-4 h-4" /> Revoke soft-delete
              </button>
            ) : (
              <button
                disabled={busy}
                onClick={handleSoftDelete}
                className="w-full px-3 py-2 rounded border border-amber-500/40 text-amber-300 text-sm hover:bg-amber-500/15 disabled:opacity-50 flex items-center justify-center gap-2"
                title="Reversible tombstone. is_active=false + deleted_at set. All PII preserved. Cashfree mandate cancelled. Revoke from this drawer."
              >
                <Trash2 className="w-4 h-4" /> Soft-delete (revocable)
              </button>
            )
          )}
          {!user.is_superuser && (
            <button
              disabled={busy}
              onClick={handleHardDelete}
              className="w-full px-3 py-2 rounded border border-rose-600/50 text-rose-400 text-sm hover:bg-rose-600/20 disabled:opacity-50 flex items-center justify-center gap-2"
              title="Permanent DB removal. Refused if the user has wallet transactions, invoices, or coded matches. Typed HARD DELETE required."
            >
              <Trash2 className="w-4 h-4" /> Hard delete (permanent)
            </button>
          )}
        </footer>
      </aside>
    </div>
  );
}

// ─── Payment section — full per-user payment surface ─────────────────────

function PaymentSection({ payments }) {
  // Render even when partially populated so the operator can see "no
  // top-ups yet" instead of a missing section. Treat every list field
  // as an array regardless of what the backend returns.
  const subs = Array.isArray(payments?.subscriptions) ? payments.subscriptions : [];
  const topUps = Array.isArray(payments?.top_ups) ? payments.top_ups : [];
  const packs = Array.isArray(payments?.pack_purchases) ? payments.pack_purchases : [];
  const refunds = Array.isArray(payments?.refunds) ? payments.refunds : [];
  const invoices = Array.isArray(payments?.invoices) ? payments.invoices : [];
  const events = Array.isArray(payments?.payment_events) ? payments.payment_events : [];
  const methods = Array.isArray(payments?.methods_used) ? payments.methods_used : [];

  if (!payments) return null;

  return (
    <section className="mt-2 space-y-4">
      <div>
        <h3 className="text-xs uppercase tracking-widest text-text-secondary mb-2 flex items-center gap-2">
          <CreditCard className="w-3.5 h-3.5" />
          Payment overview
        </h3>
        <div className="rounded border border-border-default bg-bg-secondary/30 p-2 text-xs flex flex-wrap items-center gap-2">
          <span className="text-text-secondary">Gateways used:</span>
          {methods.length === 0 ? (
            <span className="text-text-secondary italic">none yet</span>
          ) : methods.map((m) => (
            <span key={m} className="px-1.5 py-0.5 rounded bg-bg-primary border border-border-default font-mono text-[10px]">
              {m}
            </span>
          ))}
        </div>
      </div>

      {/* Subscriptions */}
      <div>
        <h4 className="text-[11px] uppercase tracking-wider text-text-secondary mb-1.5 flex items-center gap-1">
          <Repeat className="w-3 h-3" />
          Subscriptions ({subs.length})
        </h4>
        {subs.length === 0 ? (
          <div className="text-xs text-text-secondary">No subscriptions on record.</div>
        ) : (
          <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
            {subs.map((s) => <SubscriptionRow key={s.id} sub={s} />)}
          </div>
        )}
      </div>

      {/* Top-ups */}
      <div>
        <h4 className="text-[11px] uppercase tracking-wider text-text-secondary mb-1.5 flex items-center gap-1">
          <Receipt className="w-3 h-3" />
          Top-up purchases ({topUps.length})
        </h4>
        {topUps.length === 0 ? (
          <div className="text-xs text-text-secondary">No wallet top-ups.</div>
        ) : (
          <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
            {topUps.map((t) => <TopUpRow key={t.id} tx={t} />)}
          </div>
        )}
      </div>

      {/* Pack purchases */}
      <div>
        <h4 className="text-[11px] uppercase tracking-wider text-text-secondary mb-1.5 flex items-center gap-1">
          <Package className="w-3 h-3" />
          Pack purchases ({packs.length})
        </h4>
        {packs.length === 0 ? (
          <div className="text-xs text-text-secondary">No marketplace pack purchases.</div>
        ) : (
          <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
            {packs.map((p) => <PackPurchaseRow key={p.id} purchase={p} />)}
          </div>
        )}
      </div>

      {/* Refunds */}
      <div>
        <h4 className="text-[11px] uppercase tracking-wider text-text-secondary mb-1.5 flex items-center gap-1">
          <Undo2 className="w-3 h-3" />
          Refunds ({refunds.length})
        </h4>
        {refunds.length === 0 ? (
          <div className="text-xs text-text-secondary">No refund requests.</div>
        ) : (
          <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
            {refunds.map((r) => <RefundRow key={r.id} refund={r} />)}
          </div>
        )}
      </div>

      {/* Invoices (B2B) */}
      <div>
        <h4 className="text-[11px] uppercase tracking-wider text-text-secondary mb-1.5 flex items-center gap-1">
          <FileText className="w-3 h-3" />
          Invoices ({invoices.length})
        </h4>
        {invoices.length === 0 ? (
          <div className="text-xs text-text-secondary">No invoices issued.</div>
        ) : (
          <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
            {invoices.map((inv) => <InvoiceRow key={inv.id} inv={inv} />)}
          </div>
        )}
      </div>

      {/* Payment events (gateway audit trail) */}
      <div>
        <h4 className="text-[11px] uppercase tracking-wider text-text-secondary mb-1.5">
          Gateway events ({events.length})
        </h4>
        {events.length === 0 ? (
          <div className="text-xs text-text-secondary">No PaymentEvent rows linked to this user's subscriptions.</div>
        ) : (
          <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
            {events.map((e) => <PaymentEventRow key={e.id} ev={e} />)}
          </div>
        )}
      </div>
    </section>
  );
}

function SubscriptionRow({ sub }) {
  const status = sub.status || "—";
  const statusClass = status === "ACTIVE" ? "text-emerald-300"
    : status === "CANCELLED" || status === "EXPIRED" ? "text-text-secondary"
    : status === "GRACE" ? "text-amber-300"
    : "text-text-secondary";
  return (
    <div className="rounded border border-border-default bg-bg-secondary/30 px-2.5 py-1.5 text-xs">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium truncate">
            {sub.plan_name || sub.plan_code || "(unnamed plan)"}
            {sub.is_trial && <span className="ml-1.5 text-[10px] uppercase text-amber-300">trial</span>}
          </div>
          <div className="text-[10px] text-text-secondary">
            {sub.plan_code} · {sub.payment_provider || "no provider"}
            {sub.provider_sub_id && (
              <span className="ml-1 font-mono">· {sub.provider_sub_id}</span>
            )}
          </div>
        </div>
        <span className={`font-semibold ${statusClass}`}>● {status}</span>
      </div>
      <div className="text-[10px] text-text-secondary mt-0.5">
        {sub.current_period_start && (
          <span>from {sub.current_period_start.slice(0, 10)}</span>
        )}
        {sub.current_period_end && (
          <span> → {sub.current_period_end.slice(0, 10)}</span>
        )}
        {sub.custom_price_inr != null && (
          <span className="ml-2">· custom ₹{Number(sub.custom_price_inr).toLocaleString("en-IN")}</span>
        )}
        {sub.cancel_at_period_end && (
          <span className="ml-2 text-amber-300">· cancel-at-period-end</span>
        )}
      </div>
    </div>
  );
}

function TopUpRow({ tx }) {
  return (
    <div className="flex items-center justify-between text-xs px-2 py-1.5 rounded bg-bg-secondary/40">
      <div className="min-w-0 flex-1">
        <div className="truncate">
          <span className="font-mono text-emerald-400">+{tx.credits} ZC</span>
          {tx.provider && (
            <span className="ml-2 text-[10px] text-text-secondary">via {tx.provider}</span>
          )}
        </div>
        <div className="text-[10px] text-text-secondary">
          {tx.created_at && new Date(tx.created_at).toLocaleString("en-IN")}
          {tx.external_ref && (
            <span className="ml-1 font-mono">· {tx.external_ref}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function PackPurchaseRow({ purchase }) {
  return (
    <div className="flex items-center justify-between text-xs px-2 py-1.5 rounded bg-bg-secondary/40">
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{purchase.pack_name || "(pack)"}</div>
        <div className="text-[10px] text-text-secondary">
          {purchase.pack_type}
          {purchase.purchased_at && (
            <span> · {new Date(purchase.purchased_at).toLocaleDateString("en-IN")}</span>
          )}
        </div>
      </div>
      <span className="font-mono text-xs">{purchase.credits_paid} ZC</span>
    </div>
  );
}

function RefundRow({ refund }) {
  const status = refund.status || "—";
  const statusClass =
    status === "COMPLETED" ? "text-emerald-300"
    : status === "DENIED" ? "text-rose-300"
    : status === "APPROVED" || status === "PROCESSING" ? "text-amber-300"
    : "text-text-secondary";
  return (
    <div className="rounded border border-border-default bg-bg-secondary/30 px-2.5 py-1.5 text-xs">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium">
            ₹{Number(refund.amount_inr || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
          </div>
          <div className="text-[10px] text-text-secondary">
            {refund.reason_code}
            {refund.provider && <span> · {refund.provider}</span>}
            {refund.provider_refund_id && (
              <span className="ml-1 font-mono">· {refund.provider_refund_id}</span>
            )}
          </div>
        </div>
        <span className={`font-semibold ${statusClass}`}>● {status}</span>
      </div>
      {refund.customer_facing_reason && (
        <div className="text-[10px] text-text-secondary mt-0.5 italic truncate">
          "{refund.customer_facing_reason}"
        </div>
      )}
      <div className="text-[10px] text-text-secondary mt-0.5">
        {refund.created_at && `requested ${new Date(refund.created_at).toLocaleDateString("en-IN")}`}
        {refund.completed_at && ` · completed ${new Date(refund.completed_at).toLocaleDateString("en-IN")}`}
      </div>
    </div>
  );
}

function InvoiceRow({ inv }) {
  return (
    <div className="flex items-center justify-between text-xs px-2 py-1.5 rounded bg-bg-secondary/40">
      <div className="min-w-0 flex-1">
        <div className="truncate font-mono">{inv.number}</div>
        <div className="text-[10px] text-text-secondary">
          {inv.date} · {inv.status}
          {inv.period_start && inv.period_end && (
            <span> · {inv.period_start} → {inv.period_end}</span>
          )}
        </div>
      </div>
      <span className="font-mono text-xs">
        ₹{Number(inv.total_inr || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
      </span>
    </div>
  );
}

function PaymentEventRow({ ev }) {
  const statusClass =
    ev.status === "PROCESSED" ? "text-emerald-300"
    : ev.status === "FAILED" ? "text-rose-300"
    : ev.status === "DUPLICATE" ? "text-text-secondary"
    : "text-amber-300";
  return (
    <div className="rounded border border-border-default bg-bg-secondary/30 px-2 py-1 text-[11px]">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate">
            <span className="font-mono">{ev.event_type}</span>
            {ev.provider && <span className="ml-1 text-text-secondary">· {ev.provider}</span>}
          </div>
          <div className="text-[10px] text-text-secondary">
            {ev.direction === "inbound_webhook" ? "← webhook" : "→ outbound"}
            {ev.provider_object_id && (
              <span className="ml-1 font-mono">· {ev.provider_object_id}</span>
            )}
            {ev.created_at && (
              <span className="ml-1">· {new Date(ev.created_at).toLocaleString("en-IN")}</span>
            )}
          </div>
          {ev.error_message && (
            <div className="text-[10px] text-rose-300 truncate" title={ev.error_message}>
              {ev.error_message}
            </div>
          )}
        </div>
        <span className={`font-semibold ${statusClass}`}>● {ev.status}</span>
      </div>
    </div>
  );
}

function LedgerRow({ tx }) {
  const isCredit = tx.delta_credits > 0;
  return (
    <div className="flex items-center justify-between text-xs px-2 py-1.5 rounded bg-bg-secondary/40">
      <div className="min-w-0 flex-1">
        <div className="truncate">
          {tx.feature_code || tx.reason.toLowerCase()}
        </div>
        <div className="text-[10px] text-text-secondary">
          {fmtDate(tx.created_at)}
        </div>
      </div>
      <div className={`font-mono ${isCredit ? "text-emerald-400" : "text-text-primary"}`}>
        {isCredit ? "+" : ""}{tx.delta_credits} ZC
      </div>
    </div>
  );
}
